import { Box, Button } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useAuth0 } from "@auth0/auth0-react";
import {
  APP_COLOR,
  APP_HOVER_COLOR,
  BUTTON_TEXT_COLOR,
  FONT_FAMILY,
  TEXT_COLOR,
} from "../../theme";
import { clearGuestSession } from "./useIsGuest";

export interface LockedOverlayProps {
  featureName: string;
  headline: string;
  benefit: string;
  children: React.ReactNode;
  ctaLabel?: string;
  onSignup?: () => void;
}

const SURFACE = "#151718";
const BORDER = "#26292B";
const MUTED = "#889096";
const CYAN_TINT = "rgba(39,182,203,0.13)";
const CYAN_BORDER = "rgba(39,182,203,0.3)";

const LockedOverlay: React.FC<LockedOverlayProps> = ({
  featureName,
  headline,
  benefit,
  children,
  ctaLabel = "Sign up to unlock",
  onSignup,
}) => {
  const { loginWithRedirect } = useAuth0();

  const handleSignup = () => {
    if (onSignup) return onSignup();
    clearGuestSession();
    loginWithRedirect({
      appState: { targetUrl: "/initial-setting" },
      authorizationParams: { screen_hint: "signup" },
    });
  };

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%", minHeight: 480, overflow: "hidden" }}>
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          filter: "blur(7px) saturate(0.85) brightness(0.7)",
          transform: "scale(1.03)",
          pointerEvents: "none",
        }}
      >
        {children}
      </Box>
      <Box aria-hidden sx={{ position: "absolute", inset: 0, background: "rgba(8,11,17,0.62)" }} />
      <Box
        role="dialog"
        aria-labelledby="locked-overlay-headline"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 420,
          maxWidth: "calc(100% - 32px)",
          textAlign: "center",
          background: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: "18px",
          padding: "38px 34px 32px",
          boxShadow: "0 40px 110px -40px rgba(0,0,0,0.85)",
          fontFamily: FONT_FAMILY,
        }}
      >
        <Box
          sx={{
            width: 70,
            height: 70,
            margin: "0 auto 20px",
            borderRadius: "50%",
            background: CYAN_TINT,
            border: `1px solid ${CYAN_BORDER}`,
            color: APP_COLOR,
            display: "grid",
            placeItems: "center",
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 32 }} />
        </Box>
        <Box
          sx={{
            color: APP_COLOR,
            fontSize: 12.5,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            marginBottom: "9px",
            fontFamily: FONT_FAMILY,
          }}
        >
          {featureName}
        </Box>
        <Box
          id="locked-overlay-headline"
          sx={{
            color: "#FFFFFF",
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            fontFamily: FONT_FAMILY,
          }}
        >
          {headline}
        </Box>
        <Box
          sx={{
            color: MUTED,
            fontSize: 14,
            lineHeight: 1.55,
            margin: "11px 0 24px",
            fontFamily: FONT_FAMILY,
          }}
        >
          {benefit}
        </Box>
        <Button
          onClick={handleSignup}
          sx={{
            width: "100%",
            padding: "13px",
            fontSize: 15,
            fontWeight: 600,
            fontFamily: FONT_FAMILY,
            textTransform: "none",
            color: BUTTON_TEXT_COLOR,
            background: APP_COLOR,
            borderRadius: "10px",
            "&:hover": { background: APP_HOVER_COLOR },
          }}
        >
          {ctaLabel}
        </Button>
        <Box sx={{ height: 0, color: TEXT_COLOR }} />
      </Box>
    </Box>
  );
};

export default LockedOverlay;
